import { useEffect } from 'react';
import { SEVERITY_MARK, type Severity } from '../../notifications/describe';
import { notifications, useNotifications } from '../../notifications/notificationStore';
import { useActiveLogin } from '../../trading/useTrading';

const th = 'px-1.5 py-0.5 text-left font-normal';
const td = 'px-1.5 py-0.5 whitespace-nowrap';

/** Network events, so a connection hiccup reads as "Network" the way MT5's own Journal sources it. */
const isNetwork = (title: string) => title.startsWith('Connection') || title.startsWith('Price feed');
/** Everything about an order, a position or a deal. */
const isTrade = (title: string) =>
  /^(Position|Pending order|Balance|Credit|Margin call|Stop out|Order)/.test(title);

const sourceOf = (title: string): string =>
  isNetwork(title) ? 'Network' : isTrade(title) ? 'Trades' : 'Terminal';

const toneClass = (severity: Severity) =>
  severity === 'danger' ? 'text-down' : severity === 'warning' ? 'text-warn' : 'text-fg';

/**
 * MT5's own Journal: one flat table of everything the terminal told the user this session — time, source and
 * message — instead of the card-style feed the modern notification log shows.
 */
export function ClassicJournal() {
  const items = useNotifications((s) => s.items);
  const unread = useNotifications((s) => s.unread);
  const login = useActiveLogin();

  useEffect(() => {
    if (unread > 0) notifications.getState().markRead();
  }, [unread]);

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Journal">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="classic-table w-full text-xs" aria-label="Journal entries">
          <thead className="sticky top-0">
            <tr>
              <th scope="col" className={th}>
                Time
              </th>
              <th scope="col" className={th}>
                Source
              </th>
              <th scope="col" className={th}>
                Message
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={3} className="px-2 py-1.5 text-muted">
                  Nothing yet. Fills, stop levels, margin warnings and connection changes show up here.
                </td>
              </tr>
            )}
            {[...items].reverse().map((n) => {
              const mark = SEVERITY_MARK[n.severity];
              const d = new Date(n.at);
              const pad = (v: number, len = 2) => String(v).padStart(len, '0');
              const time = `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
              return (
                <tr key={n.id}>
                  <td className={`${td} num text-muted`}>
                    <span aria-hidden="true" className={toneClass(n.severity)}>
                      {mark.icon}{' '}
                    </span>
                    <span className="sr-only">{mark.label}: </span>
                    {time}
                  </td>
                  <td className={td}>{sourceOf(n.title)}</td>
                  <td className={`${td} whitespace-normal`}>
                    {login !== undefined ? `'${login}': ` : ''}
                    {n.title}
                    {n.text ? ` — ${n.text}` : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-line px-2 py-1 text-xs">
        <span className="text-muted">
          {items.length} entr{items.length === 1 ? 'y' : 'ies'} this session
        </span>
        <button
          type="button"
          className="rounded border border-line px-2 py-0.5 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
          disabled={items.length === 0}
          onClick={() => notifications.getState().clear()}
        >
          Clear
        </button>
      </div>
    </section>
  );
}
